import { useMutation } from '@apollo/client'
import type { IdVariable } from '@/types/graphql'
import { useOnErrorAuto } from '@/utils/hooks'
import type {
  CreateVehicleExpirationTypeMutation,
  CreateVehicleExpirationTypeMutationVariables,
  CreateVehicleDocumentMutation,
  CreateVehicleDocumentMutationVariables,
  CreateVehicleExpirationMutation,
  CreateVehicleExpirationMutationVariables,
  DeleteVehicleDocumentMutation,
  PatchVehicleDocumentMutation,
  PatchVehicleDocumentMutationVariables,
  EquipmentExportMutations,
  MaterialExportMutations,
  EquipmentExportMutationsInput,
} from '..'
import {
  ADD_DIESEL_CARD,
  CREATE_DUMMY_VEHICLE_MUTATION,
  CREATE_VEHICLE_BY_REGISTRATION_NUMBER_MUTATION,
  CREATE_VEHICLE_EXPIRATION_TYPE_MUTATION,
  CREATE_VEHICLE_DOCUMENT_MUTATION,
  CREATE_VEHICLE_EXPIRATION_MUTATION,
  CREATE_VEHICLE_SERVICE_PLAN_MUTATION,
  PATCH_VEHICLE_SERVICE_PLAN_MUTATION,
  DELETE_VEHICLE_SERVICE_PLAN_MUTATION,
  CONVERT_VEHICLE_EXPIRATION_TO_SERVICE_PLAN_MUTATION,
  CONVERT_VEHICLE_SERVICE_PLAN_TO_EXPIRATION_MUTATION,
  CREATE_VEHICLE_MUTATION,
  DELETE_DIESEL_CARD,
  CREATE_VEHICLE_NOTE_MUTATION,
  DELETE_VEHICLE_DOCUMENT_MUTATION,
  DELETE_VEHICLE_EXPIRATION_MUTATION,
  DELETE_VEHICLE_NOTE_MUTATION,
  PATCH_VEHICLE_DOCUMENT_MUTATION,
  PATCH_VEHICLE_EXPIRATION_MUTATION,
  PATCH_VEHICLE_MUTATION,
  UPDATE_DIESEL_CARD,
  EXPORT_EQUIPMENTS,
  EXPORT_MATERIALS,
  PATCH_VEHICLE_NOTE_MUTATION,
  DEACTIVATE_VEHICLE_MUTATION,
} from '../mutations'
import type {
  CreateDummyVehicleMutation,
  CreateDummyVehicleMutationVariables,
  CreateVehicleByRegNrMutation,
  CreateVehicleByRegNrMutationVariables,
  CreateVehicleMutation,
  CreateVehicleMutationVariables,
  CreateVehicleNoteMutation,
  CreateVehicleNoteMutationVariables,
  DeleteVehicleExpirationMutation,
  DeleteVehicleMutation,
  DeleteVehicleMutationVariables,
  DeleteVehicleNoteMutation,
  PatchVehicleDetailMutation,
  PatchVehicleDetailMutationVariables,
  PatchVehicleExpirationMutation,
  PatchVehicleExpirationMutationVariables,
  PatchVehicleNoteMutation,
  PatchVehicleNoteMutationVariables,
  CreateVehicleServicePlanMutation,
  CreateVehicleServicePlanMutationVariables,
  PatchVehicleServicePlanMutation,
  PatchVehicleServicePlanMutationVariables,
  DeleteVehicleServicePlanMutation,
  ConvertVehicleExpirationToServicePlanMutation,
  ConvertVehicleServicePlanToExpirationMutation,
} from '../types.graphql'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useNavigate } from 'react-router'

export function useVehicleMutations() {
  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()
  const translations = useTranslate({
    update: 'vehicles.toasts.vehicle-updated',
  })
  const refetchQueries = ['VehicleDetail']
  const [createVehicle] = useMutation<
    CreateVehicleMutation,
    CreateVehicleMutationVariables
  >(CREATE_VEHICLE_MUTATION)

  const [createVehicleByRegNr] = useMutation<
    CreateVehicleByRegNrMutation,
    CreateVehicleByRegNrMutationVariables
  >(CREATE_VEHICLE_BY_REGISTRATION_NUMBER_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['AllVehicles', 'AllVehiclesSelect'],
  })

  const [createDummyVehicle] = useMutation<
    CreateDummyVehicleMutation,
    CreateDummyVehicleMutationVariables
  >(CREATE_DUMMY_VEHICLE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['AllVehicles', 'AllVehiclesSelect'],
    onError: onErrorAuto(),
  })

  const [createDieselCard] = useMutation(ADD_DIESEL_CARD, {
    awaitRefetchQueries: true,
    refetchQueries,
    onCompleted: () => {
      addToast('success', translations.update)
    },
    onError(error) {
      onErrorAuto()(error)
    },
  })

  const [deleteDieselCard] = useMutation(DELETE_DIESEL_CARD, {
    awaitRefetchQueries: true,
    refetchQueries,
    onCompleted: () => {
      addToast('success', translations.update)
    },
    onError(error) {
      onErrorAuto()(error)
    },
  })

  const [patchDieselCard] = useMutation(UPDATE_DIESEL_CARD, {
    awaitRefetchQueries: true,
    refetchQueries,
    onCompleted: () => {
      addToast('success', translations.update)
    },
    onError(error) {
      onErrorAuto()(error)
    },
  })

  const [patchVehicle, { loading }] = useMutation<
    PatchVehicleDetailMutation,
    PatchVehicleDetailMutationVariables
  >(PATCH_VEHICLE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onCompleted: () => {
      addToast('success', translations.update)
    },
    onError(error) {
      onErrorAuto()(error)
    },
  })

  const [deleteVehicle] = useMutation<
    DeleteVehicleMutation,
    DeleteVehicleMutationVariables
  >(DEACTIVATE_VEHICLE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['AllVehicles', 'AllVehiclesSelect'],
  })

  return {
    loading,
    mutations: {
      create: createVehicle,
      createByRegNr: createVehicleByRegNr,
      createDummy: createDummyVehicle,
      patch: patchVehicle,
      delete: deleteVehicle,
      patchDieselCard,
      createDieselCard,
      deleteDieselCard,
    },
  }
}

export function useVehicleDocumentMutations() {
  const onErrorAuto = useOnErrorAuto()
  const refetchQueries = ['VehicleDocuments']

  const [createDocument, { loading: createLoading }] = useMutation<
    CreateVehicleDocumentMutation,
    CreateVehicleDocumentMutationVariables
  >(CREATE_VEHICLE_DOCUMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [patchDocument, { loading: patchLoading }] = useMutation<
    PatchVehicleDocumentMutation,
    PatchVehicleDocumentMutationVariables
  >(PATCH_VEHICLE_DOCUMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [deleteDocument, { loading: deleteLoading }] = useMutation<
    DeleteVehicleDocumentMutation,
    IdVariable
  >(DELETE_VEHICLE_DOCUMENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  return {
    createDocument,
    patchDocument,
    deleteDocument,
    loading: createLoading || patchLoading || deleteLoading,
  }
}

export function useVehicleExpirationMutations() {
  const onErrorAuto = useOnErrorAuto()
  const refetchQueries = ['VehicleExpirations']

  const [createExpiration, { loading: createLoading }] = useMutation<
    CreateVehicleExpirationMutation,
    CreateVehicleExpirationMutationVariables
  >(CREATE_VEHICLE_EXPIRATION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [patchExpiration, { loading: patchLoading }] = useMutation<
    PatchVehicleExpirationMutation,
    PatchVehicleExpirationMutationVariables
  >(PATCH_VEHICLE_EXPIRATION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [deleteExpiration, { loading: deleteLoading }] = useMutation<
    DeleteVehicleExpirationMutation,
    IdVariable
  >(DELETE_VEHICLE_EXPIRATION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  return {
    createExpiration,
    patchExpiration,
    deleteExpiration,
    loading: createLoading || patchLoading || deleteLoading,
  }
}

export function useVehicleServicePlanMutations() {
  const onErrorAuto = useOnErrorAuto()
  const refetchQueries = ['VehicleServicePlans']

  const [createServicePlan, { loading: createLoading }] = useMutation<
    CreateVehicleServicePlanMutation,
    CreateVehicleServicePlanMutationVariables
  >(CREATE_VEHICLE_SERVICE_PLAN_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [patchServicePlan, { loading: patchLoading }] = useMutation<
    PatchVehicleServicePlanMutation,
    PatchVehicleServicePlanMutationVariables
  >(PATCH_VEHICLE_SERVICE_PLAN_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [deleteServicePlan, { loading: deleteLoading }] = useMutation<
    DeleteVehicleServicePlanMutation,
    IdVariable
  >(DELETE_VEHICLE_SERVICE_PLAN_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  return {
    createServicePlan,
    patchServicePlan,
    deleteServicePlan,
    loading: createLoading || patchLoading || deleteLoading,
  }
}

/**
 * Moving one row between the expirations table and the services table.
 *
 * Both pages refetch: the row leaves one list and appears in the other, and the
 * per-vehicle tabs show both.
 */
export function useVehicleServicePlanConversionMutations() {
  const onErrorAuto = useOnErrorAuto()
  const refetchQueries = ['VehicleExpirations', 'VehicleServicePlans']

  const [convertExpirationToServicePlan, { loading: toServicePlanLoading }] =
    useMutation<ConvertVehicleExpirationToServicePlanMutation, IdVariable>(
      CONVERT_VEHICLE_EXPIRATION_TO_SERVICE_PLAN_MUTATION,
      {
        awaitRefetchQueries: true,
        refetchQueries,
        onError: onErrorAuto(),
      }
    )

  const [convertServicePlanToExpiration, { loading: toExpirationLoading }] =
    useMutation<ConvertVehicleServicePlanToExpirationMutation, IdVariable>(
      CONVERT_VEHICLE_SERVICE_PLAN_TO_EXPIRATION_MUTATION,
      {
        awaitRefetchQueries: true,
        refetchQueries,
        onError: onErrorAuto(),
      }
    )

  return {
    convertExpirationToServicePlan,
    convertServicePlanToExpiration,
    loading: toServicePlanLoading || toExpirationLoading,
  }
}

export const useVehicleNoteMutations = () => {
  const onErrorAuto = useOnErrorAuto()
  const refetchQueries = ['VehicleNotes']

  const [createNote, { loading: createLoading }] = useMutation<
    CreateVehicleNoteMutation,
    CreateVehicleNoteMutationVariables
  >(CREATE_VEHICLE_NOTE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [patchNote, { loading: patchLoading }] = useMutation<
    PatchVehicleNoteMutation,
    PatchVehicleNoteMutationVariables
  >(PATCH_VEHICLE_NOTE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  const [deleteNote, { loading: deleteLoading }] = useMutation<
    DeleteVehicleNoteMutation,
    IdVariable
  >(DELETE_VEHICLE_NOTE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onError: onErrorAuto(),
  })

  return {
    createNote,
    patchNote,
    deleteNote,
    loading: createLoading || patchLoading || deleteLoading,
  }
}

export function useEquipmentExportMutations(onCompleted?: () => void) {
  const translations = useTranslate({
    error: 'error.generic-server-error',
    downloadSuccess: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
  })

  const navigate = useNavigate()

  const addToast = useToast()

  const [exportEquipments, { loading }] = useMutation<
    EquipmentExportMutations,
    EquipmentExportMutationsInput
  >(EXPORT_EQUIPMENTS, {
    onCompleted(data) {
      if (data?.exportVehicles?.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else if (data?.exportVehicles?.html) {
        void navigate('/export', {
          state: { html: data?.exportVehicles?.html },
        })
      } else if (data?.exportVehicles?.ok) {
        addToast('success', translations.downloadSuccess)
      } else {
        addToast('error', translations.error)
      }
      onCompleted?.()
    },
  })

  return {
    exportEquipments,
    loading,
  }
}

export function useMaterialExportMutations(onCompleted?: () => void) {
  const translations = useTranslate({
    error: 'error.generic-server-error',
    downloadSuccess: 'exports.success-user-exports',
    exportTimeout: 'exports.export-timeout',
  })

  const navigate = useNavigate()

  const addToast = useToast()

  const [exportMaterials, { loading }] = useMutation<
    MaterialExportMutations,
    EquipmentExportMutationsInput
  >(EXPORT_MATERIALS, {
    onCompleted(data) {
      if (data?.exportMaterialFilledOutForms?.exportTimeout) {
        addToast('info', translations.exportTimeout)
      } else if (data?.exportMaterialFilledOutForms?.html) {
        void navigate('/export', {
          state: { html: data?.exportMaterialFilledOutForms?.html },
        })
      } else if (data?.exportMaterialFilledOutForms?.ok) {
        addToast('success', translations.downloadSuccess)
      } else {
        addToast('error', translations.error)
      }
      onCompleted?.()
    },
  })

  return {
    exportMaterials,
    loading,
  }
}

export function useVehicleExpirationTypeMutations() {
  const onErrorAuto = useOnErrorAuto()
  const addToast = useToast()
  const translations = useTranslate({
    added: 'vehicles.toasts.vehicle-expiration-type-added',
  })
  const refetchQueries = ['VehicleExpirationTypes']

  const [createVehicleExpirationType, { loading: createLoading }] = useMutation<
    CreateVehicleExpirationTypeMutation,
    CreateVehicleExpirationTypeMutationVariables
  >(CREATE_VEHICLE_EXPIRATION_TYPE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries,
    onCompleted: () => {
      addToast('success', translations.added)
    },
    onError: onErrorAuto(),
  })

  return {
    createVehicleExpirationType,
    loading: createLoading,
  }
}
